import { useEffect, useState } from 'react';
import { supabase } from './supabase';

// Session-scoped cache keyed by user id. Stores the in-flight Promise so
// concurrent callers from different components share a single round-trip.
// Lives until the page reloads — admin status doesn't change mid-session
// in any flow we care about.
const cache = new Map();

export const isAdmin = (userId) => {
  if (!userId) return Promise.resolve(false);
  if (cache.has(userId)) return cache.get(userId);
  const promise = supabase
    .from('admins')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle()
    .then(({ data, error }) => {
      if (error) throw error;
      return !!data;
    })
    .catch((err) => {
      console.error('[admin.isAdmin] failed:', err);
      // Default-deny on failure. Also drop the cache entry so a transient
      // failure doesn't lock the user out for the rest of the session.
      cache.delete(userId);
      return false;
    });
  cache.set(userId, promise);
  return promise;
};

export const useIsAdmin = () => {
  const [state, setState] = useState({ isAdmin: false, loading: true });

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) {
        if (!cancelled) setState({ isAdmin: false, loading: false });
        return;
      }
      const ok = await isAdmin(uid);
      if (!cancelled) setState({ isAdmin: ok, loading: false });
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const uid = session?.user?.id;
      if (!uid) {
        if (!cancelled) setState({ isAdmin: false, loading: false });
        return;
      }
      const ok = await isAdmin(uid);
      if (!cancelled) setState({ isAdmin: ok, loading: false });
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return state;
};
